import { toast } from "@/components/common/Toast"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { toastApiError } from "@/features/material-compliance/api/apiError"
import {
  notificationCategoryLabelKey,
  notificationTypeLabelKey,
  useNotificationCatalog,
  useNotificationPreferences,
  useUpdatePreference,
  type NotificationChannel,
  type NotificationPreference,
} from "@/features/notifications"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  SettingsList,
  SettingsListGroup,
  SettingsListRow,
  SettingsSwitch,
  type SettingsListColumn,
} from "../../components/SettingsList"
import { SettingsPage, SettingsSection } from "../../components/SettingsPage"
import {
  SettingsLoadError,
  SettingsSkeleton,
} from "../../components/SettingsStates"

/** The preferences. Every switch saves on its own; each channel has its own
 *  global → category → type hierarchy, and a muted scope disables the
 *  switches below it. */
function NotificationPreferences() {
  const { translate } = useTranslation()
  const inAppEnabled = useFeatureFlag("NOTIFICATIONS_IN_APP")
  const emailEnabled = useFeatureFlag("NOTIFICATIONS_EMAIL")
  const catalog = useNotificationCatalog()
  const preferences = useNotificationPreferences()
  const { mutateAsync: updatePreference } = useUpdatePreference()

  // Awaited per switch: toasts passed to `mutate` would only fire for the
  // last of several quick flips.
  async function save(
    key: string,
    channel: NotificationChannel,
    enabled: boolean,
  ) {
    try {
      await updatePreference({ key, channel, enabled })
      toast.success(translate("NOTIF/SETTINGS/TOAST_SAVED"))
    } catch (error) {
      toastApiError(error, translate, "NOTIF/SETTINGS/TOAST_SAVE_ERR")
    }
  }

  // Wait for the preferences too, so no switch flashes its default-on state.
  // Cached data first: a failed background refetch keeps the switches.
  if (!catalog.data || !preferences.data) {
    if (catalog.isLoading || preferences.isLoading) return <SettingsSkeleton />
    return (
      <SettingsLoadError
        pending={catalog.isFetching || preferences.isFetching}
        onRetry={() => {
          void catalog.refetch()
          void preferences.refetch()
        }}
      />
    )
  }

  const stored: NotificationPreference[] = preferences.data ?? []
  const isEnabled = (key: string, channel: NotificationChannel) =>
    stored.find((pref) => pref.key === key && pref.channel === channel)
      ?.enabled ?? true // no stored row = enabled

  // Only the channels this org has.
  const channels: NotificationChannel[] = [
    ...(inAppEnabled ? (["in_app"] as const) : []),
    ...(emailEnabled ? (["email"] as const) : []),
  ]
  const columns: SettingsListColumn[] = channels.map((channel) => ({
    label: translate(
      channel === "in_app"
        ? "NOTIF/SETTINGS/COL_INAPP"
        : "NOTIF/SETTINGS/COL_EMAIL",
    ),
    className: "w-20 justify-center",
  }))
  const channelSuffix = (channel: NotificationChannel) =>
    translate(
      channel === "in_app"
        ? "NOTIF/SETTINGS/CHANNEL_IN_APP_SUFFIX"
        : "NOTIF/SETTINGS/CHANNEL_EMAIL_SUFFIX",
    )

  /** One switch per channel for the setting `key`. `mutedBy` says, per
   *  channel, whether a scope above it is off; `supports` whether the type
   *  offers that channel at all (one that doesn't is never shown as on). */
  const switches = (
    key: string,
    label: string,
    mutedBy: (channel: NotificationChannel) => boolean,
    supports: (channel: NotificationChannel) => boolean = () => true,
  ) =>
    channels.map((channel) => (
      <SettingsSwitch
        key={channel}
        label={`${label}${channelSuffix(channel)}`}
        checked={supports(channel) && isEnabled(key, channel)}
        disabled={mutedBy(channel) || !supports(channel)}
        tooltip={
          supports(channel)
            ? null
            : translate("NOTIF/SETTINGS/CHANNEL_UNSUPPORTED_TOOLTIP")
        }
        onCheckedChange={(enabled) => void save(key, channel, enabled)}
      />
    ))

  const { globalKey, categories } = catalog.data
  const globalOff = (channel: NotificationChannel) =>
    !isEnabled(globalKey, channel)
  const allLabel = translate("NOTIF/SETTINGS/GLOBAL")

  return (
    <div data-agent-target="notifications.settings">
      <SettingsList columns={columns}>
        <SettingsListGroup>
          <SettingsListRow
            label={allLabel}
            cells={switches(globalKey, allLabel, () => false)}
          />
        </SettingsListGroup>
        {categories.map((category) => {
          const categoryLabel = translate(
            notificationCategoryLabelKey(category.category),
          )
          const categoryOff = (channel: NotificationChannel) =>
            globalOff(channel) || !isEnabled(category.prefKey, channel)
          return (
            <SettingsListGroup key={category.category}>
              <SettingsListRow
                label={categoryLabel}
                cells={switches(category.prefKey, categoryLabel, globalOff)}
              />
              {category.types.map((type) => {
                const typeLabel = translate(notificationTypeLabelKey(type.type))
                return (
                  <SettingsListRow
                    key={type.type}
                    nested
                    label={typeLabel}
                    cells={switches(
                      type.type,
                      typeLabel,
                      categoryOff,
                      (channel) => type.channels.includes(channel),
                    )}
                  />
                )
              })}
            </SettingsListGroup>
          )
        })}
      </SettingsList>
    </div>
  )
}

export function NotificationsPage() {
  const { translate } = useTranslation()
  return (
    <SettingsPage
      title={translate("SETTINGS/NOTIFICATIONS/TITLE")}
      description={translate("SETTINGS/NOTIFICATIONS/DESCRIPTION")}
    >
      <SettingsSection>
        <NotificationPreferences />
      </SettingsSection>
    </SettingsPage>
  )
}
